import { Box, BottomSheetHeader, Button, Pressable } from "../../../../../../../mads";
import { Close, Upload } from "../../../../../../../mads/icons";
import {
  BottomSheet,
  type BottomSheetRef,
} from "../../../../../../../shared/components/BottomSheet";
import { useI18n } from "../../../../../../../shared/I18n";

import { colors, spacing } from "@indeed/ifl-tokens/react-native";
import React, { useRef } from "react";
import { StyleSheet } from "react-native";

export interface AddResumeBottomSheetProps {
  /** Called after the sheet closes, whether from a selection or a plain dismiss. */
  onClose: () => void;
  /** Called when the user selects "Build your resume" */
  onBuildPress: () => void;
  /** Called when the user selects "Upload your resume" */
  onUploadPress: () => void;
}

/**
 * Bottom sheet presenting the "Build your resume" / "Upload your resume" choices
 * for the mobile "Add your resume" CTA in GatedMatchOverview.
 */
export const AddResumeBottomSheet: React.FC<AddResumeBottomSheetProps> = ({
  onClose,
  onBuildPress,
  onUploadPress,
}) => {
  const gt = useI18n();
  const sheetRef = useRef<BottomSheetRef>(null);

  // Animate the sheet closed before firing the caller's handler, matching the
  // close-then-act pattern used by other BottomSheet consumers (e.g. AutoApplyMenuBottomSheet).
  const handleSelectUpload = () => {
    sheetRef.current?.close(() => {
      onClose();
      onUploadPress();
    });
  };

  const handleSelectBuild = () => {
    sheetRef.current?.close(() => {
      onClose();
      onBuildPress();
    });
  };

  const handleDismiss = () => {
    sheetRef.current?.close(onClose);
  };

  const headerRight = (
    <Pressable
      onPress={handleDismiss}
      accessibilityRole="button"
      accessibilityLabel={gt.gettext("Close")}
      hitSlop={{ top: 10, right: 10, bottom: 10, left: 10 }}
      testID="vj-gated-match-overview-sheet-close-button"
      sx={{ paddingHorizontal: spacing.s3 }}
    >
      <Close size="md" color={colors.foregroundStrong} />
    </Pressable>
  );

  return (
    <BottomSheet ref={sheetRef} onClose={onClose} testID="vj-gated-match-overview-sheet">
      <BottomSheetHeader
        title={gt.gettext("Add your resume")}
        headerRight={headerRight}
        showBorder={false}
        showGrabber={false}
      />
      <Box sx={styles.sheetOptions}>
        <Button
          onPress={handleSelectUpload}
          iconBefore={<Upload size="sm" color={colors.foregroundActionInverse} />}
        >
          {gt.gettext("Upload your resume")}
        </Button>
        <Button variant="secondary" onPress={handleSelectBuild}>
          {gt.gettext("Build your resume")}
        </Button>
      </Box>
    </BottomSheet>
  );
};

const styles = StyleSheet.create({
  sheetOptions: {
    padding: spacing.s4,
    paddingBottom: spacing.s6,
    gap: spacing.s2,
  },
});
